import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { KeyRound, ShieldCheck, SunMoon, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const outlet = useOutletContext() || {};
  const adminTheme = outlet.adminTheme || "dark";
  const setAdminTheme = outlet.setAdminTheme;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="admin-settings-page">
      <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Settings</h1>
      <p className="mt-1 text-sm text-neutral-500">Profile and account security</p>

      <section className="glass mt-8 rounded-3xl p-7">
        <div className="flex items-center gap-4">
          <div className="glass flex h-14 w-14 items-center justify-center rounded-2xl font-display text-lg font-bold">
            {user?.name?.[0] || "J"}
          </div>
          <div>
            <p className="font-semibold text-white" data-testid="settings-name">{user?.name}</p>
            <p className="text-sm text-neutral-500" data-testid="settings-email">{user?.email}</p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-[11px] font-medium text-emerald-300">
            <ShieldCheck className="h-3.5 w-3.5" /> Admin
          </span>
        </div>
      </section>

      <section className="glass mt-6 rounded-3xl p-7">
        <p className="eyebrow mb-6 flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" /> Change Password</p>
        <form onSubmit={submit} className="space-y-4">
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Current password"
            autoComplete="current-password"
            className="input-glass"
            data-testid="settings-current-password"
          />
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="New password (min 8 characters)"
            autoComplete="new-password"
            className="input-glass"
            data-testid="settings-new-password"
          />
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
            className="input-glass"
            data-testid="settings-confirm-password"
          />
          <button type="submit" disabled={saving} className="btn-primary !py-2.5 text-xs disabled:opacity-50" data-testid="settings-save-button">
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </section>

      <section className="glass mt-6 rounded-3xl p-7" data-testid="settings-appearance">
        <p className="eyebrow mb-6 flex items-center gap-2"><SunMoon className="h-3.5 w-3.5" /> Appearance</p>
        <p className="mb-4 text-sm text-neutral-500">Default CMS theme — your choice is remembered on this browser.</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setAdminTheme?.("dark")}
            data-testid="settings-theme-dark"
            className={`flex flex-1 items-center justify-center gap-2 rounded-2xl border px-5 py-4 text-sm font-medium transition-colors ${
              adminTheme !== "light"
                ? "border-white bg-white text-black"
                : "border-white/12 bg-white/[0.03] text-neutral-400 hover:border-white/30 hover:text-white"
            }`}
          >
            <Moon className="h-4 w-4" /> Dark
          </button>
          <button
            type="button"
            onClick={() => setAdminTheme?.("light")}
            data-testid="settings-theme-light"
            className={`flex flex-1 items-center justify-center gap-2 rounded-2xl border px-5 py-4 text-sm font-medium transition-colors ${
              adminTheme === "light"
                ? "border-white bg-white text-black"
                : "border-white/12 bg-white/[0.03] text-neutral-400 hover:border-white/30 hover:text-white"
            }`}
          >
            <Sun className="h-4 w-4" /> Light
          </button>
        </div>
      </section>
    </div>
  );
}
